import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Popconfirm, Typography, Tag, Select, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { PencilSimple, Trash, Eye } from '@phosphor-icons/react';
import { useProducts, Product } from '@hooks';
import { ProductFormModal } from './ProductFormModal';

const { Title, Paragraph } = Typography;
const { Option } = Select;

export const ProductsManagementPage = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>();

  const { products, deleteProduct } = useProducts({
    page: currentPage,
    limit: pageSize,
    search: searchQuery || undefined,
    category: categoryFilter || undefined,
    orderBy: sortField,
    orderType: sortOrder,
  });

  const handleAdd = () => {
    setEditingProduct(null);
    setIsModalVisible(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProduct.mutateAsync(id);
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const handleViewProduct = (product: Product) => {
    navigate(`/merch/${product.id}`);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingProduct(null);
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numPrice);
  };

  const getProductPriceRange = (product: Product): string => {
    if (!product.variants || product.variants.length === 0) {
      return product.price ? formatPrice(product.price) : '-';
    }
    
    const prices = product.variants.map(v => Number(v.price));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return formatPrice(minPrice);
    }
    
    return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
  };

  const getTotalStock = (product: Product): number => {
    if (!product.variants || product.variants.length === 0) {
      return product.stockQuantity || 0;
    }
    
    return product.variants.reduce((total, variant) => total + (variant.stockQuantity || 0), 0);
  };

  const getProductImage = (product: Product): string => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2';
    
    if (product.images && product.images.length > 0) {
      const img = product.images[0];
      return img.startsWith('http') ? img : `https://${bucketName}.s3.${region}.amazonaws.com/${img}`;
    }
    
    if (product.imageUrl) {
      return product.imageUrl.startsWith('http') 
        ? product.imageUrl 
        : `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }
    
    return `https://placehold.co/80x80/1a1a1a/00ade6?text=${encodeURIComponent(product.name)}`;
  };

  const columns = [
    {
      title: <span className="font-azonix">IMAGE</span>,
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      render: (_: any, product: Product) => (
        <img
          src={getProductImage(product)}
          alt={product.name}
          className="w-16 h-16 object-cover"
          onError={(e) => {
            e.currentTarget.src = `https://placehold.co/80x80/1a1a1a/00ade6?text=${encodeURIComponent(product.name)}`;
          }}
        />
      ),
    },
    {
      title: <span className="font-azonix">NAME</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string) => (
        <span className="font-coda">{name}</span>
      ),
    },
    {
      title: <span className="font-azonix">CATEGORY</span>,
      dataIndex: 'category',
      key: 'category',
      sorter: true,
      render: (category: string) => (
        category ? <Tag color="blue" className="!rounded-none">{category}</Tag> : '-'
      ),
    },
    {
      title: <span className="font-azonix">PRICE</span>,
      dataIndex: 'price',
      key: 'price',
      sorter: true,
      render: (_: any, product: Product) => (
        <span className="font-coda text-[#00ade6]">
          {getProductPriceRange(product)}
        </span>
      ),
    },
    {
      title: <span className="font-azonix">STOCK</span>,
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      sorter: true,
      render: (_: any, product: Product) => {
        const totalStock = getTotalStock(product);
        return (
          <Tag color={totalStock > 10 ? 'green' : totalStock > 0 ? 'orange' : 'red'} className="!rounded-none">
            {totalStock}
          </Tag>
        );
      },
    },
    {
      title: <span className="font-azonix">VARIANTS</span>,
      dataIndex: 'variants',
      key: 'variants',
      sorter: true,
      render: (variants: any[]) => (
        <span className="font-coda">{variants?.length || 0}</span>
      ),
    },
    {
      title: <span className="font-azonix">ACTIONS</span>,
      key: 'actions',
      width: 150,
      render: (_: any, record: Product) => (
        <div className="flex gap-2">
          <Tooltip title="View in Shop">
            <Button
              type="text"
              icon={<Eye size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewProduct(record);
              }}
              className="text-primary hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#00ade6]"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<PencilSimple size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(record);
              }}
              className="text-primary hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#00ade6]"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title={<span className="font-azonix !font-normal ">Delete Product</span>}
              description={<span>Are you sure you want to delete this product?<br />This action cannot be undone.</span>}
              onConfirm={() => handleDelete(record.id)}
              okText="Yes, delete"
              cancelText="No"
              overlayClassName="remove-position-popconfirm"
              okButtonProps={{ className: "btn-single-cut-cancel", danger: true }}
              cancelButtonProps={{ className: "btn-single-cut" }}
            >
              <Button
                type="text"
                danger
                icon={<Trash size={16} />}
                className="!text-red-500 hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#ef4444]"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  // Extract unique categories from products
  const categories = products.data?.data
    ? [...new Set(products.data.data.map(p => p.category).filter(Boolean))]
    : [];

  return (
    <div className="estabrook-bg pt-25 pb-10 md:pb-20">
      {/* Hero Section */}
      <section className="w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <Title level={1} className="font-azonix !font-normal text-primary text-center tracking-wider pb-4 !mb-0">
            PRODUCTS MANAGEMENT
          </Title>
          <div className="max-w-3xl mx-auto !mb-8">
            <Paragraph className="text-center text-lg text-light">
              Manage your product catalog, inventory and pricing
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="w-full py-12 bg-charcoal">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-3">
            <Title level={2} className="font-azonix !font-normal text-white">PRODUCTS LIST</Title>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              className="btn-single-cut !text-sm sm:!text-base !h-9 sm:!h-10 !px-4 sm:!px-6 w-fit self-end sm:self-auto"
              onClick={handleAdd}
            >
              ADD PRODUCT
            </Button>
          </div>
          <Paragraph className="text-white !mb-8">
            View, edit or remove products from the system
          </Paragraph>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {/* Custom Search Input */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <SearchOutlined className="!text-[#00ade6]" style={{ color: '#00ade6' }} />
              </div>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-72 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-[#00ade6] focus:ring-1 focus:ring-[#00ade6]/50 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
                >
                  ×
                </button>
              )}
            </div>
            
            {/* Category Select */}
            <Select
              placeholder={
                <span className="flex items-center gap-2 text-zinc-500">
                  <FilterOutlined className="!text-[#00ade6]" style={{ color: '#00ade6' }} /> Category
                </span>
              }
              allowClear={!!categoryFilter}
              value={categoryFilter || undefined}
              onChange={(value) => setCategoryFilter(value || '')}
              className={`w-full sm:w-48 [&_.ant-select-selector]:!bg-zinc-800/80 [&_.ant-select-selector]:!border-zinc-700 [&_.ant-select-selector]:!h-11 [&_.ant-select-selection-placeholder]:!text-zinc-500 [&_.ant-select-selection-item]:!text-white [&_.ant-select-arrow]:!text-[#00ade6] [&_.ant-select-clear]:!bg-zinc-800 [&_.ant-select-clear]:!text-[#00ade6] [&_.ant-select-clear_svg]:!fill-[#00ade6] [&_.ant-select-clear]:!top-[60%] ${categoryFilter ? '[&_.ant-select-arrow]:!hidden' : '[&_.ant-select-arrow]:!top-[55%]'}`}
              size="large"
              popupClassName="!bg-zinc-800 !border-zinc-700 [&_.ant-select-item]:!text-zinc-300 [&_.ant-select-item-option-active]:!bg-zinc-700 [&_.ant-select-item-option-selected]:!bg-[#00ade6]/20 [&_.ant-select-item-option-selected]:!text-[#00ade6]"
            >
              {categories.length > 0 ? (
                categories.map((cat) => (
                  <Option key={cat} value={cat}>{cat}</Option>
                ))
              ) : (
                <>
                  <Option value="Apparel">Apparel</Option>
                  <Option value="Equipment">Equipment</Option>
                  <Option value="Tools">Tools</Option>
                  <Option value="Accessories">Accessories</Option>
                </>
              )}
            </Select>
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-zinc-900/50 border border-zinc-800 shadow-xl overflow-x-auto hide-scrollbar">
              <Table
                dataSource={products.data?.data || []}
                columns={columns}
                rowKey="id"
                loading={products.isLoading}
                onChange={(_pagination, _filters, sorter) => {
                  if (Array.isArray(sorter)) {
                    // Multiple sorters (not used in this case)
                    return;
                  }
                  
                  if (sorter && sorter.field) {
                    const field = sorter.field as string;
                    const order = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined;
                    setSortField(field);
                    setSortOrder(order);
                    setCurrentPage(1); // Reset to first page when sorting changes
                  } else {
                    setSortField(undefined);
                    setSortOrder(undefined);
                    setCurrentPage(1);
                  }
                }}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: products.data?.pagination?.totalCount || 0,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  },
                  showSizeChanger: true,
                  showTotal: (total) => <span className="text-white font-coda">Total <span className="text-white font-coda">{total}</span> products</span>,
                  className: "!bg-transparent [&_.ant-pagination-item]:!bg-zinc-800 [&_.ant-pagination-item]:!border-zinc-700 [&_.ant-pagination-item-active]:!bg-transparent [&_.ant-pagination-item-active]:!border-zinc-700 [&_.ant-pagination-item_a]:!text-white [&_.ant-pagination-item-active_a]:!text-white [&_.ant-pagination-prev_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-next_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-prev_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-next_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-prev_.ant-pagination-item-link]:!text-white [&_.ant-pagination-next_.ant-pagination-item-link]:!text-white",
                }}
                className="[&_.ant-table]:!bg-transparent [&_.ant-table-thead_th]:!bg-zinc-800/50 [&_.ant-table-thead_th]:!text-white [&_.ant-table-thead_th]:!border-b-[#00ade6] [&_.ant-table-thead_th]:!font-azonix [&_.ant-table-tbody_td]:!bg-transparent [&_.ant-table-tbody_td]:!border-b-zinc-700 [&_.ant-table-tbody_td]:!text-white [&_.ant-table-row:hover_td]:!bg-zinc-800/50 [&_.ant-table-placeholder]:!bg-transparent [&_.ant-empty-description]:!text-white min-w-[800px]"
              />
          </div>

          {/* Product Form Modal */}
          <ProductFormModal
            visible={isModalVisible}
            onClose={handleModalClose}
            product={editingProduct}
          />
        </div>
      </section>
    </div>
  );
};

